import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const relatedArticles = articles
    .filter((a) => a.slug !== slug && a.category === article?.category)
    .slice(0, 3);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen pt-16 flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-heading font-bold text-foreground-800 mb-2">文章未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">请检查文章链接是否正确</p>
            <Link
              to="/"
              className="px-6 py-2.5 bg-primary-500 text-background-50 dark:text-foreground-950 rounded-full text-sm font-medium hover:bg-primary-600 transition-all whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <article className="pt-16">
        <Breadcrumb
          items={[
            { label: '精选文章', href: '/article/2024-guohuo-shouji-pingce' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <header className="w-full relative h-[350px] md:h-[450px] flex items-end overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20"></div>
          <div className="relative z-10 w-full px-4 md:px-6 pb-10 md:pb-14">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.category}`}
                  className="px-3 py-1 bg-primary-500 text-background-50 dark:text-foreground-950 text-xs font-medium rounded-full"
                >
                  {article.categoryName}
                </Link>
                {article.tags.slice(0, 3).map((tag) => (
                  <span key={tag} className="text-xs text-white/70">
                    #{tag}
                  </span>
                ))}
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-4 leading-tight">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-white/70">
                <span>{article.author}</span>
                <span>{article.date}</span>
                <span>{article.readTime}</span>
              </div>
            </div>
          </div>
        </header>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base
                prose-strong:text-foreground-900
                prose-lead:text-foreground-600
                [&_p]:mb-5 [&_p]:text-base [&_p]:leading-loose
                [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-heading [&_h2]:font-bold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>
        </section>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.slug}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:shadow-md transition-all cursor-pointer"
                  >
                    <div className="relative w-full h-44 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm md:text-base font-medium text-foreground-900 mb-2 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {ra.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span>{ra.date}</span>
                        <span>{ra.readTime}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.excerpt,
            author: {
              '@type': 'Person',
              name: article.author,
            },
            datePublished: article.date,
            image: article.coverImage,
            publisher: {
              '@type': 'Organization',
              name: '精品国产',
            },
          }),
        }}
      />
    </>
  );
}